import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import * as XLSX from 'xlsx'

const fmt  = n => new Intl.NumberFormat('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}).format(n||0)
const fmtN = n => new Intl.NumberFormat('en-US',{maximumFractionDigits:0}).format(n||0)

const CHANGE_TYPES  = ['Addition','Omission','Substitution','Design Change','Unforeseen','Client Instruction']
const STATUSES      = ['draft','submitted','under_review','approved','rejected','on_hold']
const IMPACT_PROG   = ['None','Minor','Moderate','Major']

const STATUS_COLORS = {
  draft:        'bg-[#EDE7DA] text-[#6B7280]',
  submitted:    'bg-blue-100 text-blue-700',
  under_review: 'bg-yellow-100 text-yellow-700',
  approved:     'bg-green-100 text-[#1A7A4A]',
  rejected:     'bg-red-100 text-[#A32D2D]',
  on_hold:      'bg-orange-100 text-orange-700',
}

const FIDIC_CLAUSES = ['13.1','13.2','13.3','13.4','13.5','13.6','13.7','1.9','4.12','8.4','20.1']

const emptyChange = {
  change_ref:'', tq_rfi_ref:'', drawing_ref_old:'', drawing_ref_new:'',
  change_description:'', element_type:'', location:'', change_type:'Design Change',
  cost_code:'', initiated_by:'', fidic_clause:'', engineer_instruction_ref:'',
  date_tq_issued:'', date_engineer_response:'', date_vo_raised:'', date_approved:'',
  status:'draft', impact_on_program:'None', eot_ref:'', remarks:''
}

const emptyLine = {
  cost_code:'', item_description:'', nrm2_ref:'', unit:'',
  original_qty:'', revised_qty:'', budget_rate:'', agreed_rate:'',
  omitted_amount:'', added_amount:'', prelims_impact:'', cash_flow_month:''
}

export default function TechnicalChangeRegister({ projectId: initProjectId }) {
  const [projects,    setProjects]    = useState([])
  const [projectId,   setProjectId]   = useState(null)
  const [projectName, setProjectName] = useState('')
  const { can } = useProjectAuth(projectId)
  const [contractValue,setContractValue]= useState(0)
  const [allImpacts,  setAllImpacts]   = useState([])
  const [changes,     setChanges]     = useState([])
  const [loading,     setLoading]     = useState(false)
  const [view,        setView]        = useState('list') // list | detail
  const [selected,    setSelected]    = useState(null)
  const [impacts,     setImpacts]     = useState([])
  const [showModal,   setShowModal]   = useState(false)
  const [editChange,  setEditChange]  = useState(null)
  const [form,        setForm]        = useState(emptyChange)
  const [filterStatus,setFilterStatus]= useState('all')
  const [filterType,  setFilterType]  = useState('all')
  const [saving,      setSaving]      = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [uploading,   setUploading]   = useState(false)
  const [uploadResult,setUploadResult]= useState(null)
  const fileInputRef = useRef(null)
  // Cost impact modal
  const [showImpact,  setShowImpact]  = useState(false)
  const [impactForm,  setImpactForm]  = useState(emptyLine)
  const [editImpact,  setEditImpact]  = useState(null)

  useEffect(() => {
    supabase.from('projects').select('id,name').order('name').then(({ data }) => setProjects(data || []))
  }, [])

  useEffect(() => {
    if (initProjectId) {
      setProjectId(initProjectId)
      supabase.from('projects').select('name,contract_value').eq('id', initProjectId).single().then(({ data }) => { setProjectName(data?.name || ''); setContractValue(data?.contract_value || 0) })
    }
  }, [initProjectId])

  useEffect(() => { if (projectId) fetchChanges() }, [projectId])

  function downloadTemplate() {
    const wb = XLSX.utils.book_new()
    const logH = ['change_ref','tq_rfi_ref','change_description','change_type','fidic_clause','location','initiated_by','engineer_instruction_ref','eot_ref','status','impact_on_program','date_tq_issued','date_engineer_response','date_vo_raised','date_approved','remarks']
    const logD = [
      ['VO-001','TQ-012','Additional piling works — unforeseen soil conditions','Unforeseen','4.12','Pile Cap Zone A','Structural Engineer','EI-001','','approved','Major','2024-03-10','2024-03-18','2024-03-25','2024-04-02','Confirmed by geotech report'],
      ['VO-002','TQ-015','Omission of temporary access road','Omission','13.1','Site Entrance','Cost Controller','EI-002','','approved','Minor','2024-03-20','2024-03-28','2024-04-05','2024-04-10','Replaced by permanent road'],
      ['VO-003','RFI-008','Aquarium main tank structural upgrade','Design Change','13.3','Aquarium Hall B2','Design Engineer','EI-003','','under_review','Major','2024-04-01','2024-04-12','2024-04-20','','Pending client sign-off'],
      ['VO-004','CI-003','Upgrade MEP spec in VIP areas','Client Instruction','13.2','VIP Lounge L3','Client','EI-004','','submitted','Minor','2024-04-15','','2024-04-28','','Quotation submitted'],
      ['VO-005','TQ-021','Additional facade cladding — east elevation','Addition','13.1','East Elevation','Architect','EI-005','','approved','Moderate','2024-05-01','2024-05-10','2024-05-18','2024-05-25','Approved MM-014'],
    ]
    const ws1 = XLSX.utils.aoa_to_sheet([logH, ...logD])
    ws1['!cols'] = logH.map((_,i) => ({ wch: i===2?35:i===15?28:14 }))
    XLSX.utils.book_append_sheet(wb, ws1, 'Change_Log')
    const impH = ['change_ref','item_description','cost_code','nrm2_ref','unit','original_qty','revised_qty','budget_rate','agreed_rate','prelims_impact','cash_flow_month']
    const impD = [
      ['VO-001','Additional piling — Type 3 bored piles','03-STRUCT','3.4.1','m','380','520','8500','9200','185000','2024-04-01'],
      ['VO-001','Pile cap enlargement — Zone A','03-STRUCT','3.4.2','m3','120','180','650','720','22000','2024-04-01'],
      ['VO-002','Omit temporary access road','02-CIVIL','3.2.1','m2','2400','0','180','180','',''],
      ['VO-005','Facade cladding — east elevation','05-ARCH','5.2.3','m2','0','1450','1600','1750','52000','2024-06-01'],
      ['VO-005','Steel support framing — east elevation','03-STRUCT','3.3.1','ton','0','18','12000','13500','','2024-06-01'],
    ]
    const ws2 = XLSX.utils.aoa_to_sheet([impH, ...impD])
    ws2['!cols'] = impH.map((_,i) => ({ wch: i===1?32:14 }))
    XLSX.utils.book_append_sheet(wb, ws2, 'Cost_Impact_Lines')
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['Field','Valid Values'],
      ['change_type','Addition | Omission | Substitution | Design Change | Unforeseen | Client Instruction'],
      ['status','draft | submitted | under_review | approved | rejected | on_hold'],
      ['impact_on_program','None | Minor | Moderate | Major'],
      ['fidic_clause','13.1 | 13.2 | 13.3 | 13.4 | 13.5 | 13.6 | 13.7 | 1.9 | 4.12 | 8.4 | 20.1'],
    ]), 'Valid_Values')
    XLSX.writeFile(wb, 'ChangeRegister_Template.xlsx')
  }

  async function handleUpload(e) {
    const file = e.target.files[0]
    if (!file || !projectId) return
    setUploading(true); setUploadResult(null)
    try {
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf)
      const ws1 = wb.Sheets['Change_Log']
      const rows1 = ws1 ? XLSX.utils.sheet_to_json(ws1, { defval: '' }) : []
      let imported = 0, skipped = 0, impactImported = 0
      const errors = []
      for (const row of rows1) {
        const desc = (row['change_description'] || '').toString().trim()
        if (!desc) { skipped++; continue }
        const { error } = await supabase.from('technical_changes').insert({
          project_id: projectId,
          change_ref: row['change_ref']?.toString().trim() || null,
          tq_rfi_ref: row['tq_rfi_ref']?.toString().trim() || null,
          change_description: desc,
          change_type: row['change_type']?.toString().trim() || 'Design Change',
          fidic_clause: row['fidic_clause']?.toString().trim() || null,
          location: row['location']?.toString().trim() || null,
          initiated_by: row['initiated_by']?.toString().trim() || null,
          engineer_instruction_ref: row['engineer_instruction_ref']?.toString().trim() || null,
          eot_ref: row['eot_ref']?.toString().trim() || null,
          status: row['status']?.toString().trim() || 'draft',
          impact_on_program: row['impact_on_program']?.toString().trim() || 'None',
          date_tq_issued: row['date_tq_issued']?.toString().trim() || null,
          date_engineer_response: row['date_engineer_response']?.toString().trim() || null,
          date_vo_raised: row['date_vo_raised']?.toString().trim() || null,
          date_approved: row['date_approved']?.toString().trim() || null,
          remarks: row['remarks']?.toString().trim() || null,
        })
        if (error) { errors.push(`${row['change_ref'] || desc}: ${error.message}`); skipped++ }
        else imported++
      }
      const ws2 = wb.Sheets['Cost_Impact_Lines']
      const rows2 = ws2 ? XLSX.utils.sheet_to_json(ws2, { defval: '' }) : []
      for (const row of rows2) {
        const ref = (row['change_ref'] || '').toString().trim()
        const desc = (row['item_description'] || '').toString().trim()
        if (!ref || !desc) continue
        const { data: ch } = await supabase.from('technical_changes').select('id')
          .eq('project_id', projectId).eq('change_ref', ref).maybeSingle()
        if (!ch) { errors.push(`Impact "${desc}": ref "${ref}" not found`); continue }
        const oq = parseFloat(row['original_qty']) || 0
        const rq = parseFloat(row['revised_qty']) || 0
        const ar = parseFloat(row['agreed_rate']) || 0
        const cfm = (() => { const v = row['cash_flow_month']?.toString().trim(); if (!v) return null; return /^\d{4}-\d{2}$/.test(v) ? v + '-01' : v })()
        const { error: e2 } = await supabase.from('change_cost_impact').insert({
          change_id: ch.id, item_description: desc,
          cost_code: row['cost_code']?.toString().trim() || null,
          nrm2_ref: row['nrm2_ref']?.toString().trim() || null,
          unit: row['unit']?.toString().trim() || null,
          original_qty: oq, revised_qty: rq,
          budget_rate: parseFloat(row['budget_rate']) || 0, agreed_rate: ar,
          omitted_amount: oq > rq ? (oq - rq) * ar : 0,
          added_amount: rq > oq ? (rq - oq) * ar : 0,
          prelims_impact: parseFloat(row['prelims_impact']) || 0,
          cash_flow_month: cfm,
        })
        if (!e2) impactImported++
        else errors.push(`Impact "${desc}": ${e2.message}`)
      }
      setUploadResult({ imported, skipped, impactImported, errors })
      await fetchChanges()
    } catch(err) {
      setUploadResult({ imported: 0, skipped: 0, impactImported: 0, errors: [err.message] })
    }
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function fetchChanges() {
    setLoading(true)
    const { data } = await supabase.from('technical_changes').select('*').eq('project_id', projectId).order('created_at', { ascending: false })
    setChanges(data || [])
    // fetch all cost impacts for this project for summary bar
    const ids = (data || []).map(c => c.id)
    if (ids.length > 0) {
      const { data: imp } = await supabase.from('change_cost_impact').select('change_id,added_amount,omitted_amount').in('change_id', ids)
      setAllImpacts(imp || [])
    } else {
      setAllImpacts([])
    }
    setLoading(false)
  }

  async function fetchImpacts(changeId) {
    const { data } = await supabase.from('change_cost_impact').select('*').eq('change_id', changeId).order('created_at')
    setImpacts(data || [])
  }

  async function openDetail(ch) {
    setSelected(ch)
    await fetchImpacts(ch.id)
    setView('detail')
  }

  async function saveChange() {
    if (!form.change_description || !projectId) return
    setSaving(true)
    const payload = {
      project_id: projectId,
      ...form,
      date_tq_issued:           form.date_tq_issued || null,
      date_engineer_response:   form.date_engineer_response || null,
      date_vo_raised:           form.date_vo_raised || null,
      date_approved:            form.date_approved || null,
      fidic_clause:             form.fidic_clause || null,
      eot_ref:                  form.eot_ref || null,
    }
    let savedId = editChange?.id
    if (editChange) {
      await supabase.from('technical_changes').update(payload).eq('id', editChange.id)
    } else {
      const { data } = await supabase.from('technical_changes').insert(payload).select().single()
      savedId = data?.id
    }
    setSaving(false)
    setShowModal(false)
    setEditChange(null)
    setForm(emptyChange)
    await fetchChanges()
    if (savedId) {
      const { data: ch } = await supabase.from('technical_changes').select('*').eq('id', savedId).single()
      if (ch) openDetail(ch)
    }
  }

  async function deleteChange(id) {
    if (!window.confirm('Delete this change record?')) return
    await supabase.from('technical_changes').delete().eq('id', id)
    fetchChanges()
    if (selected?.id === id) { setView('list'); setSelected(null) }
  }

  async function bulkDelete() {
    if (selectedIds.size === 0) return
    if (!window.confirm(`Delete ${selectedIds.size} change(s)? This cannot be undone.`)) return
    await supabase.from('technical_changes').delete().in('id', [...selectedIds])
    setSelectedIds(new Set())
    fetchChanges()
  }

  function toggleSelect(id, e) {
    e.stopPropagation()
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }

  function toggleSelectAll() {
    setSelectedIds(selectedIds.size === filtered.length ? new Set() : new Set(filtered.map(c => c.id)))
  }

  async function saveImpact() {
    if (!impactForm.item_description || !selected) return
    setSaving(true)
    const oq = parseFloat(impactForm.original_qty)||0, rq = parseFloat(impactForm.revised_qty)||0
    const br = parseFloat(impactForm.budget_rate)||0, ar = parseFloat(impactForm.agreed_rate)||0
    const omit = oq > rq ? (oq-rq)*ar : 0
    const add  = rq > oq ? (rq-oq)*ar : 0
    const payload = {
      change_id:       selected.id,
      cost_code:       impactForm.cost_code || null,
      item_description:impactForm.item_description,
      nrm2_ref:        impactForm.nrm2_ref || null,
      unit:            impactForm.unit || null,
      original_qty:    oq,
      revised_qty:     rq,
      budget_rate:     br,
      agreed_rate:     ar,
      omitted_amount:  impactForm.omitted_amount ? parseFloat(impactForm.omitted_amount) : omit,
      added_amount:    impactForm.added_amount   ? parseFloat(impactForm.added_amount)   : add,
      prelims_impact:  parseFloat(impactForm.prelims_impact)||0,
      cash_flow_month: impactForm.cash_flow_month || null,
    }
    if (editImpact) {
      await supabase.from('change_cost_impact').update(payload).eq('id', editImpact.id)
    } else {
      await supabase.from('change_cost_impact').insert(payload)
    }
    setSaving(false)
    setShowImpact(false)
    setEditImpact(null)
    setImpactForm(emptyLine)
    fetchImpacts(selected.id)
  }

  async function deleteImpact(id) {
    if (!window.confirm('Delete this line?')) return
    await supabase.from('change_cost_impact').delete().eq('id', id)
    fetchImpacts(selected.id)
  }

  const filtered = changes.filter(c => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false
    if (filterType !== 'all' && c.change_type !== filterType) return false
    return true
  })

  // KPIs
  const approved    = changes.filter(c => c.status === 'approved').length
  const pending     = changes.filter(c => ['submitted','under_review'].includes(c.status)).length
  const totalNet    = impacts.reduce((s,i) => s + (i.added_amount||0) - (i.omitted_amount||0), 0)
  // Contract summary
  const approvedIds  = new Set(changes.filter(c => c.status === 'approved').map(c => c.id))
  const pendingIds   = new Set(changes.filter(c => ['submitted','under_review'].includes(c.status)).map(c => c.id))
  const approvedNet  = allImpacts.filter(i => approvedIds.has(i.change_id)).reduce((s,i) => s + (i.added_amount||0) - (i.omitted_amount||0), 0)
  const pendingNet   = allImpacts.filter(i => pendingIds.has(i.change_id)).reduce((s,i) => s + (i.added_amount||0) - (i.omitted_amount||0), 0)
  const revisedContract = contractValue + approvedNet

  // Impact totals for detail view
  const impactTotalAdd  = impacts.reduce((s,i) => s + (i.added_amount||0), 0)
  const impactTotalOmit = impacts.reduce((s,i) => s + (i.omitted_amount||0), 0)
  const impactNet       = impactTotalAdd - impactTotalOmit
  const impactPrelims   = impacts.reduce((s,i) => s + (i.prelims_impact||0), 0)

  if (view === 'detail' && selected) {
    return (
      <div className="p-6 min-h-screen" style={{ background: '#F5F1EA', color: '#1C1A18', fontFamily: 'Inter, sans-serif' }}>
        {/* Back */}
        <button onClick={() => { setView('list'); setSelected(null) }} className="flex items-center gap-2 text-sm mb-4 hover:opacity-70" style={{ color: '#6B655C' }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="10,4 6,8 10,12"/></svg>
          Back to Register
        </button>

        {/* Change Header */}
        <div className="rounded-xl border p-5 mb-5" style={{ borderColor: '#EDE7DA', background: '#EDE7DA' }}>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="font-mono text-sm" style={{ color: '#6B655C' }}>{selected.change_ref || '—'}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selected.status]||''}`}>
                  {selected.status?.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                </span>
                {selected.change_type && <span className="px-2 py-0.5 rounded bg-[#EDE7DA] text-xs" style={{ color: '#6B655C' }}>{selected.change_type}</span>}
              </div>
              <h2 className="text-lg font-bold" style={{ color: '#1C1A18' }}>{selected.change_description}</h2>
              {selected.location && <p className="text-sm mt-0.5" style={{ color: '#6B655C' }}>📍 {selected.location}</p>}
            </div>
            <div className="flex gap-2">
              {can('TechnicalChange.Edit') && (
                <button onClick={() => { setEditChange(selected); setForm({...emptyChange,...selected}); setShowModal(true) }}
                  className="px-3 py-1.5 rounded-lg text-sm border" style={{ borderColor: '#EDE7DA', background: 'white' }}>Edit</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-sm">
            {selected.tq_rfi_ref && <div><span className="text-xs" style={{ color: '#6B655C' }}>TQ/RFI Ref</span><p className="font-mono">{selected.tq_rfi_ref}</p></div>}
            {selected.fidic_clause && <div><span className="text-xs" style={{ color: '#6B655C' }}>FIDIC Clause</span><p>{selected.fidic_clause}</p></div>}
            {selected.engineer_instruction_ref && <div><span className="text-xs" style={{ color: '#6B655C' }}>EI Ref</span><p>{selected.engineer_instruction_ref}</p></div>}
            {selected.eot_ref && <div><span className="text-xs" style={{ color: '#6B655C' }}>EOT Ref</span><p>{selected.eot_ref}</p></div>}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-xs" style={{ color: '#6B655C' }}>
            {selected.date_tq_issued && <div>TQ Issued: {selected.date_tq_issued}</div>}
            {selected.date_engineer_response && <div>Response: {selected.date_engineer_response}</div>}
            {selected.date_vo_raised && <div>VO Raised: {selected.date_vo_raised}</div>}
            {selected.date_approved && <div>Approved: {selected.date_approved}</div>}
          </div>
        </div>

        {/* Cost Impact */}
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#EDE7DA' }}>
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: '#EDE7DA', borderColor: '#EDE7DA' }}>
            <span className="font-semibold text-sm">Cost Impact</span>
            <button onClick={() => { setEditImpact(null); setImpactForm(emptyLine); setShowImpact(true) }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ background: '#1A7A4A' }}>
              + Add Line
            </button>
          </div>
          {impacts.length === 0 ? (
            <div className="p-6 text-center text-sm" style={{ color: '#6B655C' }}>No cost impact lines yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: '#EDE7DA', color: '#6B655C' }}>
                    <th className="px-3 py-2 text-left">Description</th>
                    <th className="px-3 py-2 text-left">NRM2</th>
                    <th className="px-3 py-2 text-left">Unit</th>
                    <th className="px-3 py-2 text-right">Orig Qty</th>
                    <th className="px-3 py-2 text-right">Rev Qty</th>
                    <th className="px-3 py-2 text-right">Budget Rate</th>
                    <th className="px-3 py-2 text-right">Agreed Rate</th>
                    <th className="px-3 py-2 text-right">Omitted</th>
                    <th className="px-3 py-2 text-right">Added</th>
                    <th className="px-3 py-2 text-right">Prelims</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {impacts.map((line, idx) => (
                    <tr key={line.id} style={{ borderTop: idx>0?'1px solid var(--cream)':'none' }}>
                      <td className="px-3 py-2">{line.item_description}</td>
                      <td className="px-3 py-2 font-mono" style={{ color: '#6B655C' }}>{line.nrm2_ref||'—'}</td>
                      <td className="px-3 py-2">{line.unit||'—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{line.original_qty||0}</td>
                      <td className="px-3 py-2 text-right font-mono">{line.revised_qty||0}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtN(line.budget_rate)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtN(line.agreed_rate)}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: '#A32D2D' }}>{line.omitted_amount ? `(${fmtN(line.omitted_amount)})` : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: '#1A7A4A' }}>{line.added_amount ? fmtN(line.added_amount) : '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">{line.prelims_impact ? fmtN(line.prelims_impact) : '—'}</td>
                      <td className="px-2 py-2">
                        <div className="flex gap-1">
                          <button onClick={() => { setEditImpact(line); setImpactForm({...emptyLine,...line}); setShowImpact(true) }} className="p-0.5 hover:bg-[#EDE7DA] rounded">
                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M11 2l3 3-9 9H2v-3L11 2z"/></svg>
                          </button>
                          <button onClick={() => deleteImpact(line.id)} className="p-0.5 hover:bg-red-50 rounded" style={{ color: '#A32D2D' }}>
                            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#EDE7DA', borderTop: '2px solid #DDD9D0' }}>
                    <td colSpan={7} className="px-3 py-2 font-semibold text-xs">Totals</td>
                    <td className="px-3 py-2 text-right font-bold font-mono text-xs" style={{ color: '#A32D2D' }}>({fmtN(impactTotalOmit)})</td>
                    <td className="px-3 py-2 text-right font-bold font-mono text-xs" style={{ color: '#1A7A4A' }}>{fmtN(impactTotalAdd)}</td>
                    <td className="px-3 py-2 text-right font-bold font-mono text-xs">{fmtN(impactPrelims)}</td>
                    <td></td>
                  </tr>
                  <tr style={{ background: '#EDE7DA' }}>
                    <td colSpan={7} className="px-3 py-2 font-bold text-xs">Net Cost Impact (excl. Prelims)</td>
                    <td colSpan={3} className="px-3 py-2 text-right font-bold font-mono text-sm" style={{ color: impactNet >= 0 ? '#1A7A4A' : '#A32D2D' }}>
                      SAR {impactNet >= 0 ? '' : '('}{fmtN(Math.abs(impactNet))}{impactNet < 0 ? ')' : ''}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Impact Line Modal */}
        {showImpact && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(28,26,24,0.5)' }}>
            <div className="rounded-2xl shadow-xl w-full max-w-xl mx-4 overflow-hidden max-h-[90vh] flex flex-col" style={{ background: '#F5F1EA' }}>
              <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#EDE7DA' }}>
                <h2 className="font-semibold text-sm">{editImpact ? 'Edit Impact Line' : 'Add Cost Impact Line'}</h2>
                <button onClick={() => { setShowImpact(false); setEditImpact(null); setImpactForm(emptyLine) }} className="p-1 rounded hover:bg-[#EDE7DA]">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
                </button>
              </div>
              <div className="px-5 py-4 space-y-3 overflow-y-auto text-sm">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Description *</label>
                  <input value={impactForm.item_description} onChange={e => setImpactForm(f=>({...f,item_description:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>NRM2 Ref</label>
                    <input value={impactForm.nrm2_ref} onChange={e => setImpactForm(f=>({...f,nrm2_ref:e.target.value}))}
                      placeholder="e.g. F10" className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Unit</label>
                    <input value={impactForm.unit} onChange={e => setImpactForm(f=>({...f,unit:e.target.value}))}
                      placeholder="m², m, t…" className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Cost Code</label>
                    <input value={impactForm.cost_code} onChange={e => setImpactForm(f=>({...f,cost_code:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Original Qty</label>
                    <input type="number" value={impactForm.original_qty} onChange={e => setImpactForm(f=>({...f,original_qty:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Revised Qty</label>
                    <input type="number" value={impactForm.revised_qty} onChange={e => setImpactForm(f=>({...f,revised_qty:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Budget Rate</label>
                    <input type="number" value={impactForm.budget_rate} onChange={e => setImpactForm(f=>({...f,budget_rate:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Agreed Rate</label>
                    <input type="number" value={impactForm.agreed_rate} onChange={e => setImpactForm(f=>({...f,agreed_rate:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Prelims Impact (SAR)</label>
                    <input type="number" value={impactForm.prelims_impact} onChange={e => setImpactForm(f=>({...f,prelims_impact:e.target.value}))}
                      placeholder="Auto-calc if blank" className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Cash Flow Month</label>
                    <input type="date" value={impactForm.cash_flow_month} onChange={e => setImpactForm(f=>({...f,cash_flow_month:e.target.value}))}
                      className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                  </div>
                </div>
                {/* Auto-calc preview */}
                {(impactForm.revised_qty || impactForm.original_qty) && impactForm.agreed_rate && (
                  <div className="rounded-lg p-3 text-xs" style={{ background: '#EDE7DA' }}>
                    <span style={{ color: '#6B655C' }}>Auto-calc: </span>
                    {(() => {
                      const oq=parseFloat(impactForm.original_qty)||0, rq=parseFloat(impactForm.revised_qty)||0, ar=parseFloat(impactForm.agreed_rate)||0
                      const omit = oq>rq ? (oq-rq)*ar : 0, add = rq>oq ? (rq-oq)*ar : 0
                      return <span>Omitted: <b style={{color:'#A32D2D'}}>({fmtN(omit)})</b> · Added: <b style={{color:'#1A7A4A'}}>{fmtN(add)}</b> · Net: <b>{fmtN(add-omit)}</b></span>
                    })()}
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: '#EDE7DA' }}>
                <button onClick={() => { setShowImpact(false); setEditImpact(null); setImpactForm(emptyLine) }}
                  className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: '#EDE7DA' }}>Cancel</button>
                <button onClick={saveImpact} disabled={saving || !impactForm.item_description}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: '#1A7A4A' }}>
                  {saving ? 'Saving…' : (editImpact ? 'Update' : 'Add Line')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ── List View
  return (
    <div className="p-6 min-h-screen" style={{ background: '#F5F1EA', color: '#1C1A18', fontFamily: 'Inter, sans-serif' }}>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Technical Change Register</h1>
          {projectName && <p className="text-sm mt-0.5" style={{ color: '#6B655C' }}>{projectName}</p>}
        </div>
        <div className="flex items-center gap-3">
          {!initProjectId && (
            <select value={projectId || ''} onChange={e => { setProjectId(e.target.value); supabase.from('projects').select('name,contract_value').eq('id',e.target.value).single().then(({data})=>{ setProjectName(data?.name||''); setContractValue(data?.contract_value||0) }) }}
              className="border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA', background: 'white' }}>
              <option value="">Select project…</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button onClick={downloadTemplate}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border"
            style={{ borderColor: '#EDE7DA', background: 'white', color: '#4B5563' }}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,7 8,10 11,7"/><line x1="8" y1="2" x2="8" y2="10"/></svg>
            Template
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload}/>
          {can('TechnicalChange.Create') && (
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading || !projectId}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border"
              style={{ borderColor: '#1B3A6B', background: uploading ? '#E5E7EB' : 'white', color: '#1B3A6B' }}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M2 11v2a1 1 0 001 1h10a1 1 0 001-1v-2"/><polyline points="5,6 8,3 11,6"/><line x1="8" y1="3" x2="8" y2="11"/></svg>
              {uploading ? 'Uploading…' : 'Upload Excel'}
            </button>
          )}
          {can('TechnicalChange.Create') && (
            <button onClick={() => { setEditChange(null); setForm(emptyChange); setShowModal(true) }}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ background: '#1A7A4A' }}>
              + New Change
            </button>
          )}
        </div>
      </div>
      {uploadResult && (
        <div className="mb-4 rounded-xl border p-4 text-sm" style={{ background: uploadResult.errors.length ? '#FFF7ED' : '#F0FDF4', borderColor: uploadResult.errors.length ? '#FED7AA' : '#BBF7D0' }}>
          <div className="flex items-center justify-between mb-1">
            <span className="font-semibold" style={{ color: uploadResult.errors.length ? '#B45309' : '#15803D' }}>
              {uploadResult.errors.length === 0 ? '✓ Upload successful' : '⚠ Upload completed with issues'}
            </span>
            <button onClick={() => setUploadResult(null)} className="text-xs" style={{ color: '#6B7280' }}>✕</button>
          </div>
          <div className="text-xs" style={{ color: '#374151' }}>
            Changes imported: <b>{uploadResult.imported}</b> · Impact lines imported: <b>{uploadResult.impactImported}</b> · Skipped: <b>{uploadResult.skipped}</b>
          </div>
          {uploadResult.errors.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {uploadResult.errors.map((e,i) => <div key={i} className="text-xs" style={{ color: '#B45309' }}>• {e}</div>)}
            </div>
          )}
        </div>
      )}

      {/* Contract Summary Bar */}
      {contractValue > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4 rounded-xl border p-4" style={{ background: '#EDE7DA', borderColor: '#D6CFBF' }}>
          <div>
            <p className="text-xs mb-0.5" style={{ color: '#6B655C' }}>Original Contract Value</p>
            <p className="text-base font-bold font-mono" style={{ color: '#1C1A18' }}>SAR {fmt(contractValue)}</p>
          </div>
          <div>
            <p className="text-xs mb-0.5" style={{ color: '#6B655C' }}>Approved VOs Net</p>
            <p className="text-base font-bold font-mono" style={{ color: approvedNet >= 0 ? '#1A7A4A' : '#A32D2D' }}>
              {approvedNet >= 0 ? '+' : ''}{fmt(approvedNet)}
            </p>
          </div>
          <div>
            <p className="text-xs mb-0.5 font-semibold" style={{ color: '#1B3A6B' }}>Revised Contract Value</p>
            <p className="text-base font-bold font-mono" style={{ color: '#1B3A6B' }}>SAR {fmt(revisedContract)}</p>
          </div>
          <div>
            <p className="text-xs mb-0.5" style={{ color: '#6B655C' }}>Pending VOs</p>
            <p className="text-base font-bold font-mono" style={{ color: '#B06D00' }}>
              {pendingNet >= 0 ? '+' : ''}{fmt(pendingNet)}
            </p>
          </div>
        </div>
      )}
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Changes',  val: changes.length,  color: '#1C1A18' },
          { label: 'Approved',        val: approved,         color: '#1A7A4A' },
          { label: 'Pending Review',  val: pending,          color: '#B06D00' },
          { label: 'Rejected / On Hold', val: changes.filter(c=>['rejected','on_hold'].includes(c.status)).length, color: '#A32D2D' },
        ].map(k => (
          <div key={k.label} className="rounded-xl p-4 border" style={{ background: '#EDE7DA', borderColor: '#EDE7DA' }}>
            <p className="text-xs mb-1" style={{ color: '#6B655C' }}>{k.label}</p>
            <p className="text-2xl font-bold" style={{ color: k.color }}>{k.val}</p>
          </div>
        ))}
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between mb-3 px-4 py-2.5 rounded-xl" style={{ background: '#FFF0F0', border: '1px solid #FECACA' }}>
          <span className="text-sm font-medium" style={{ color: '#A32D2D' }}>{selectedIds.size} change{selectedIds.size > 1 ? 's' : ''} selected</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: '#FECACA', background: 'white', color: '#6B7280' }}>Cancel</button>
            <button onClick={bulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white" style={{ background: '#A32D2D' }}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
              Delete {selectedIds.size} selected
            </button>
          </div>
        </div>
      )}
      {/* Table */}
      <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#EDE7DA' }}>
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: '#EDE7DA', borderColor: '#EDE7DA' }}>
          <span className="font-semibold text-sm">Change Log</span>
          <div className="flex gap-2">
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              className="border rounded-lg px-2 py-1 text-xs" style={{ borderColor: '#EDE7DA', background: 'white' }}>
              <option value="all">All Types</option>
              {CHANGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="border rounded-lg px-2 py-1 text-xs" style={{ borderColor: '#EDE7DA', background: 'white' }}>
              <option value="all">All Statuses</option>
              {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
            </select>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm" style={{ color: '#6B655C' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm" style={{ color: '#6B655C' }}>No changes yet — click "+ New Change" to start</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: '#EDE7DA', color: '#6B655C', fontSize: 11 }}>
                <th className="px-3 py-2 text-center" onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={filtered.length > 0 && selectedIds.size === filtered.length} onChange={toggleSelectAll} style={{ cursor: 'pointer', accentColor: '#1B3A6B' }}/>
                </th>
                <th className="px-4 py-2 text-left">Ref</th>
                <th className="px-4 py-2 text-left">Description</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">FIDIC</th>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-left">Program Impact</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-left">Date VO</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ch, idx) => (
                <tr key={ch.id} className="cursor-pointer hover:bg-[#FAF8F5]"
                  style={{ borderTop: idx>0?'1px solid var(--cream)':'none' }}
                  onClick={() => openDetail(ch)}>
                  <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.has(ch.id)} onChange={e => toggleSelect(ch.id, e)} style={{ cursor: 'pointer', accentColor: '#1B3A6B' }}/>
                  </td>
                  <td className="px-4 py-2.5 text-xs font-mono" style={{ color: '#6B655C' }}>{ch.change_ref||'—'}</td>
                  <td className="px-4 py-2.5 max-w-xs">
                    <div className="truncate">{ch.change_description}</div>
                    {ch.tq_rfi_ref && <div className="text-xs" style={{ color: '#6B655C' }}>TQ: {ch.tq_rfi_ref}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: '#6B655C' }}>{ch.change_type||'—'}</td>
                  <td className="px-4 py-2.5 text-xs font-mono" style={{ color: '#6B655C' }}>{ch.fidic_clause||'—'}</td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: '#6B655C' }}>{ch.location||'—'}</td>
                  <td className="px-4 py-2.5 text-xs">
                    {ch.impact_on_program && ch.impact_on_program !== 'None' && (
                      <span className="px-1.5 py-0.5 rounded text-xs" style={{ background: ch.impact_on_program==='Major'?'#FFEDED':ch.impact_on_program==='Moderate'?'#FFF7ED':'#FEFCE8', color: ch.impact_on_program==='Major'?'#A32D2D':ch.impact_on_program==='Moderate'?'#B06D00':'#6B655C' }}>
                        {ch.impact_on_program}
                      </span>
                    )}
                    {(!ch.impact_on_program || ch.impact_on_program==='None') && <span style={{ color: '#6B655C' }}>None</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ch.status]||''}`}>
                      {ch.status?.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-xs" style={{ color: '#6B655C' }}>{ch.date_vo_raised||'—'}</td>
                  <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                    {can('TechnicalChange.Delete') && (
                      <button onClick={() => deleteChange(ch.id)} className="p-1 rounded hover:bg-red-50" style={{ color: '#A32D2D' }}>
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><polyline points="3,4 13,4"/><path d="M5 4V2h6v2"/><path d="M4 4l1 10h6l1-10"/></svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Change Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(28,26,24,0.5)' }}>
          <div className="rounded-2xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden max-h-[90vh] flex flex-col" style={{ background: '#F5F1EA' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: '#EDE7DA' }}>
              <h2 className="font-semibold">{editChange ? 'Edit Change' : 'New Technical Change'}</h2>
              <button onClick={() => { setShowModal(false); setEditChange(null); setForm(emptyChange) }} className="p-1 rounded hover:bg-[#EDE7DA]">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/></svg>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Change Ref</label>
                  <input value={form.change_ref} onChange={e => setForm(f=>({...f,change_ref:e.target.value}))}
                    placeholder="TCR-001" className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>TQ / RFI Ref</label>
                  <input value={form.tq_rfi_ref} onChange={e => setForm(f=>({...f,tq_rfi_ref:e.target.value}))}
                    placeholder="TQ-001" className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Description *</label>
                <textarea value={form.change_description} onChange={e => setForm(f=>({...f,change_description:e.target.value}))}
                  rows={2} className="w-full border rounded-lg px-3 py-2 text-sm resize-none" style={{ borderColor: '#EDE7DA' }} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Change Type</label>
                  <select value={form.change_type} onChange={e => setForm(f=>({...f,change_type:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA', background: 'white' }}>
                    {CHANGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>FIDIC Clause</label>
                  <select value={form.fidic_clause} onChange={e => setForm(f=>({...f,fidic_clause:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA', background: 'white' }}>
                    <option value="">—</option>
                    {FIDIC_CLAUSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Program Impact</label>
                  <select value={form.impact_on_program} onChange={e => setForm(f=>({...f,impact_on_program:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA', background: 'white' }}>
                    {IMPACT_PROG.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Location</label>
                  <input value={form.location} onChange={e => setForm(f=>({...f,location:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Status</label>
                  <select value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA', background: 'white' }}>
                    {STATUSES.map(s => <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,c=>c.toUpperCase())}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>EI / Engineer Instruction Ref</label>
                  <input value={form.engineer_instruction_ref} onChange={e => setForm(f=>({...f,engineer_instruction_ref:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>EOT Ref (if applicable)</label>
                  <input value={form.eot_ref} onChange={e => setForm(f=>({...f,eot_ref:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Date TQ Issued</label>
                  <input type="date" value={form.date_tq_issued} onChange={e => setForm(f=>({...f,date_tq_issued:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color: '#6B655C' }}>Date VO Raised</label>
                  <input type="date" value={form.date_vo_raised} onChange={e => setForm(f=>({...f,date_vo_raised:e.target.value}))}
                    className="w-full border rounded-lg px-3 py-2 text-sm" style={{ borderColor: '#EDE7DA' }} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: '#EDE7DA' }}>
              <button onClick={() => { setShowModal(false); setEditChange(null); setForm(emptyChange) }}
                className="px-4 py-2 rounded-lg text-sm border" style={{ borderColor: '#EDE7DA' }}>Cancel</button>
              <button onClick={saveChange} disabled={saving || !form.change_description}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ background: '#1A7A4A' }}>
                {saving ? 'Saving…' : (editChange ? 'Update' : 'Create Change')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
